import { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Divider, Menu, MenuItem, Stack } from "@mui/material";
import DeleteCreditCard from "./DeleteCreditCard";
import { CustomDataGrid } from "../../../../components/CustomDataGrid";
import { baseURL } from "../../../../config/api";

const filterItems = [
    {
        id: 0,
        label: "Card Holder Name",
    },
    {
        id: 1,
        label: "Last Four Digits",
    },
]

export const CreditCards = () => {
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [searchTableData, setSearchTableData] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [quotesFilterValue, setQuotesFilterValue] = useState(0);
    const [particularCreditDetails, setParticularCreditDetails] = useState<any>();
    const [editPopup, setEditPopup] = useState<boolean>(false);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [selectedFilter, setSelectedFilter] = useState<string>(filterItems[0].label);
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const columnFilterOpen = Boolean(columnFilterAnchorEl);
    const initialColumns: GridColDef[] = [
        {
            field: "id",
            headerName: "#",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "paymentGateway",
            headerName: "Payment Gateway",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "cardholderName",
            headerName: "Cardholder's Name",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <span
                        className="underline text-[#0000ff] cursor-pointer"
                        onClick={() => {
                            setEditPopup(true);
                            setParticularCreditDetails(params.row);
                        }}
                    >
                        {params?.value}
                    </span>
                );
            },
        },
        {
            field: "brand",
            headerName: "Brand",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "expirationDate",
            headerName: "Expiration Date",
            editable: false,
            sortable: false,
            flex: 1,
        },
        {
            field: "creditCardNumber",
            headerName: "Last 4 Digits",
            flex: 1,
            editable: false,
            sortable: false,
        }
    ];
    const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
    const [selectedColumns, setSelectedColumns] = useState(initialColumns?.map(col => col?.headerName));

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColumnFilterAnchorEl(event.currentTarget);
    };
    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const toggleColumn = (headerName: string) => {
        setSelectedColumns(prev =>
            prev?.includes(headerName) ? prev?.filter(col => col !== headerName) : [...prev, headerName]
        );
    };

    useEffect(() => {
        const filterCol = initialColumns?.filter(col => selectedColumns?.includes(col?.headerName));
        setColumns(filterCol);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedColumns])

    const toggleAll = () => {
        if (selectedColumns?.length === initialColumns?.length) {
            setSelectedColumns([]);
        } else {
            setSelectedColumns(initialColumns?.map(col => col?.headerName));
        }
    };

    useEffect(() => {
        if (searchValue === "" && !isFirstTime) {
            const newPaginationModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
            };

            setPaginationModel(newPaginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue === "") {
            setIsFirstTime(false);
            emptySearchApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const emptySearchApiCall = (newPaginationModel: any) => {
        setLoading(true);
        fetch(`${baseURL}creditCard/GetCreditCardReportPaging/${newPaginationModel.page},${newPaginationModel.pageSize}`)
            .then((res) => res.json())
            .then((data) => {
                const result = data.creditCardReport.map((item: any) => {
                    return {
                        ...item,
                        paymentGateway: item.paymentGateway ? item.paymentGateway : ""
                    };
                });

                setSearchTableData(result);
                setTotalLength(data.count);
                setLoading(false);
            })
            .catch((error) => {
                setSearchTableData([]);
                setLoading(false);
            });
    }

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                const newPaginationModel = {
                    page: 0,
                    pageSize: paginationModel?.pageSize,
                };

                setPaginationModel(newPaginationModel);
            }, 1000);
        }

        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue !== "") {
            apiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const apiCall = (newPaginationModel: any) => {
        let filterData: any[] = [];
        setLoading(true);
        switch (quotesFilterValue) {
            case 0:
                fetch(`${baseURL}creditCard/GetCreditCardReportSearchWithCardholderName/${searchValue},${newPaginationModel.page},${newPaginationModel.pageSize}`)
                    .then((res) => res.json())
                    .then((data) => {
                        filterData = data.creditCardReport.map((item: any) => {
                            return {
                                ...item,
                                paymentGateway: item.paymentGateway ? item.paymentGateway : ""
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                    })
                    .catch(() => {
                        setSearchTableData([]);
                        setLoading(false);
                    })
                break;
            case 1:
                fetch(`${baseURL}creditCard/GetCreditCardReportSearchWithLast4Digits/${searchValue},${newPaginationModel.page},${newPaginationModel.pageSize}`)
                    .then((res) => res.json())
                    .then((data) => {
                        filterData = data.creditCardReport.map((item: any) => {
                            return {
                                ...item,
                                paymentGateway: item.paymentGateway ? item.paymentGateway : ""
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                    })
                    .catch(() => {
                        setSearchTableData([]);
                        setLoading(false);
                    })
                break;
        }
    }

    return (
        <>
            <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
                <div className="w-full bg-white p-4 rounded-md">
                    <div className={`flex justify-between items-center p-4 border-b border-secondary`}>
                        <div className="flex items-center gap-1.5">

                            <h2 className={`text-lg font-semibold text-secondary`}>Credit Cards</h2>

                            <div>
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid border-secondary`}
                                    aria-controls={columnFilterOpen ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={columnFilterOpen ? 'true' : undefined}
                                    onClick={handleColumnFilterClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={`text-secondary text-[14px] font-medium`}>Columns</span>
                                        <span className={`material-symbols-outlined text-[18px] font-light text-secondary`}>
                                            filter_alt
                                        </span>
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={columnFilterAnchorEl}
                                    open={columnFilterOpen}
                                    onClose={handleColumnFilterClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 170,
                                                maxHeight: 300,
                                                backgroundColor: "white"
                                            }
                                        }
                                    }}
                                >
                                    <div className="flex items-center gap-2 px-[16px] py-[6px] w-full">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox cursor-pointer"
                                            checked={selectedColumns?.length === initialColumns?.length ? true : false}
                                            onClick={toggleAll}
                                        />
                                        <span className={`text-secondary font-medium text-[12px]`}>Select All</span>
                                    </div>

                                    <Divider className="w-full" />

                                    {
                                        initialColumns?.map((column, index) => ([
                                            <MenuItem
                                                key={`${index}-Item`}
                                                sx={{
                                                    fontSize: "12px",
                                                    marginBlock: "0px",
                                                    "&: hover": {
                                                        cursor: "default"
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-checkbox cursor-pointer"
                                                        checked={selectedColumns.includes(column?.headerName)}
                                                        onChange={() => toggleColumn(column?.headerName || "")}
                                                    />
                                                    <span className={`text-secondary font-medium`}>{column?.headerName}</span>
                                                </div>
                                            </MenuItem>,
                                            <Divider key={`${index}-Divider`} className="w-full" />
                                        ]))
                                    }
                                </Menu>
                            </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                            <div>
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid border-secondary`}
                                    aria-controls={open ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={`text-secondary text-[14px] font-medium`}>{selectedFilter}</span>
                                        {open ? <span className={`material-symbols-outlined text-secondary text-[25px] font-extralight`}>
                                            keyboard_arrow_up
                                        </span> : <span className={`material-symbols-outlined text-secondary text-[25px] font-extralight`}>
                                            keyboard_arrow_down
                                        </span>}
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 180,
                                                backgroundColor: "white"
                                            }
                                        }
                                    }}
                                >
                                    {
                                        filterItems?.map((filter, index) => ([
                                            <MenuItem
                                                key={`${index}-Item`}
                                                sx={{ fontSize: "12px", marginBlock: "0px" }}
                                                onClick={() => {
                                                    setSelectedFilter(filter.label)
                                                    setQuotesFilterValue(filter.id)
                                                    setAnchorEl(null);

                                                }}>
                                                <span className={`text-secondary text-[16px] font-medium`}>{filter.label}</span>
                                            </MenuItem>,
                                            <Divider key={`${index}-Divider`} className="w-full" />
                                        ]))
                                    }
                                </Menu>
                            </div>

                            <div className={`flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border border-secondary shadow-sm`}>
                                <span className={`material-symbols-outlined text-[25px] font-light text-secondary`}>
                                    search
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className={`border-none outline-none w-full text-secondary bg-transparent`}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <CustomDataGrid
                        filteredRows={searchTableData}
                        columns={columns?.map(col => ({
                            ...col,
                            headerClassName: "orange",
                        }))}
                        loading={loading}
                        totalLength={totalLength}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                    />
                </div>
            </div>

            <DeleteCreditCard
                editPopup={editPopup}
                hidePopup={() => setEditPopup(false)}
                deleteCreditCard={() => {
                    setEditPopup(false);
                    setPaginationModel({
                        page: 0,
                        pageSize: 10,
                    })
                }}
                particularCreditDetails={particularCreditDetails}
            />
        </>

    )
}
